import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import {
  boolean,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';

export type FieldType =
  | 'string'
  | 'password'
  | 'number'
  | 'currency'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'enum'
  | 'relation'
  | 'multi-relation'
  | 'component'
  | 'file';

export interface ColumnDef {
  name: string;
  label: string;
  type: FieldType;
  sortable?: boolean;
  /** Só pra type: 'multi-relation' — coluna vira badges buscados via GET /:resource/:id/:relation.resource. */
  relation?: { resource: string; displayField: string };
  /** Chave no component-registry, escape hatch para células fora do genérico. */
  component?: string;
  /** Máscaras (ver src/lib/mask.ts) — a exibida é a que melhor encaixa no tamanho do valor. */
  mask?: string[];
  /** Só pra type: 'enum' — traduz o valor bruto (ex: "item") pro label (ex: "Item do pedido"). */
  options?: { label: string; value: string }[];
}

export interface FilterDef {
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date' | 'period';
  options?: { label: string; value: string }[];
  relation?: { resource: string; displayField: string };
}

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  colSpan?: { sm?: number; md?: number; lg?: number };
  section?: string;
  options?: { label: string; value: string }[];
  relation?: { resource: string; displayField: string };
  /** Chave no component-registry, escape hatch para campos fora do genérico. */
  component?: string;
  /** Só pra type: 'file' — valor do atributo `accept` do input (ex: "application/pdf,image/*"). */
  accept?: string;
  /** Máscaras (ver src/lib/mask.ts e use-mask-input) — só no front, o backend recebe sem máscara. */
  mask?: string[];
  showOnCreate?: boolean;
  showOnEdit?: boolean;
  showOnView?: boolean;
  disabledOnEdit?: boolean;
}

export interface ScreenLayout {
  columns: ColumnDef[];
  filters: FilterDef[];
  fields: FieldDef[];
}

export const screenActionValues = [
  'browse',
  'create',
  'read',
  'update',
  'delete',
] as const;
export type ScreenAction = (typeof screenActionValues)[number];

export const screens = pgTable('screens', {
  id: uuid('id')
    .primaryKey()
    .$defaultFn(() => Bun.randomUUIDv7()),
  resource: text('resource').notNull().unique(),
  label: text('label').notNull(),
  pluralLabel: text('plural_label').notNull(),
  icon: text('icon'),
  menuOrder: integer('menu_order').notNull().default(0),
  menuGroup: text('menu_group'),
  showInMenu: boolean('show_in_menu').notNull().default(true),
  actions: text('actions')
    .array()
    .notNull()
    .default(screenActionValues as unknown as string[]),
  layout: jsonb('layout').notNull().$type<ScreenLayout>(),
  isActive: boolean('is_active').notNull().default(true),
  createdAt: timestamp('created_at')
    .notNull()
    .$defaultFn(() => new Date()),
  updatedAt: timestamp('updated_at')
    .$onUpdate(() => new Date())
    .notNull(),
});

export type Screen = InferSelectModel<typeof screens>;
export type CreateScreenInput = InferInsertModel<typeof screens>;
export type UpdateScreenInput = Partial<CreateScreenInput>;
